import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AvailabilityPage() {
  const [slots, setSlots] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState("75");
  const [repeat, setRepeat] = useState("none");
  const [occurrences, setOccurrences] = useState("4");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/slots/mine").then((r) => setSlots(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!date || !time) { toast.error("Choose a date and time."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/slots", {
        start: `${date}T${time}`, duration: parseInt(duration, 10),
        repeat, occurrences: parseInt(occurrences, 10),
      });
      toast.success(data.created > 1 ? `${data.created} recurring slots added.` : "Availability added.");
      setDate(""); setTime(""); setRepeat("none");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/slots/${id}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <DashboardShell title="Availability" subtitle="Open slots appear to clients the moment their questionnaires are complete.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl">
        <form onSubmit={add} className="card-warm p-6 space-y-4 h-fit" data-testid="add-slot-form">
          <p className="font-heading text-xl font-semibold">Add a slot</p>
          <div className="space-y-2"><Label>Date</Label>
            <Input type="date" data-testid="slot-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          <div className="space-y-2"><Label>Start time</Label>
            <Input type="time" data-testid="slot-time-input" value={time} onChange={(e) => setTime(e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          <div className="space-y-2"><Label>Duration</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="slot-duration-select" className="border-[#E5E5DF]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="60">60 minutes</SelectItem>
                <SelectItem value="75">75 minutes</SelectItem>
                <SelectItem value="90">90 minutes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Repeat</Label>
            <Select value={repeat} onValueChange={setRepeat}>
              <SelectTrigger data-testid="slot-repeat-select" className="border-[#E5E5DF]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Does not repeat</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {repeat !== "none" && (
            <div className="space-y-2"><Label>Repeat for</Label>
              <Select value={occurrences} onValueChange={setOccurrences}>
                <SelectTrigger data-testid="slot-occurrences-select" className="border-[#E5E5DF]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["2", "4", "6", "8", "12", "24"].map((n) => <SelectItem key={n} value={n}>{n} occurrences</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button type="submit" disabled={saving} data-testid="add-slot-btn"
            className="w-full rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
            <Plus className="h-4 w-4 mr-2" /> {saving ? "Adding…" : "Add availability"}
          </Button>
        </form>
        <div className="lg:col-span-2 space-y-3" data-testid="slots-list">
          {slots === null ? (
            <div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" />
          ) : slots.length === 0 ? (
            <div className="card-warm p-10 text-center"><p className="text-sm text-[#5C5C57]">No slots yet. Add your first availability window.</p></div>
          ) : slots.map((s) => (
            <div key={s.id} className="card-warm px-6 py-4 flex items-center justify-between" data-testid={`slot-row-${s.id}`}>
              <div>
                <p className="font-semibold text-sm">{new Date(s.start).toLocaleString()}</p>
                <p className="text-xs text-[#5C5C57]">{s.duration} min · {s.status === "open" ? "Open" : "Booked"}</p>
                {s.status === "booked" && s.client_name && (
                  <p className="text-xs text-[#1C1C19] mt-1" data-testid={`slot-client-${s.id}`}>
                    <span className="font-semibold">{s.client_name}</span>
                    {s.referral_question ? ` — ${s.referral_question}` : s.product_name ? ` — ${s.product_name}` : ""}
                  </p>
                )}
              </div>
              {s.status === "open" ? (
                <Button variant="ghost" size="sm" data-testid={`delete-slot-${s.id}`} onClick={() => remove(s.id)}
                  className="text-[#8C2A2A] hover:bg-red-50 rounded-full"><Trash2 className="h-4 w-4" /></Button>
              ) : (
                <span className="text-xs font-bold text-[#1E3F20] bg-[#E3DAC9]/60 rounded-full px-3 py-1">Booked</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}
